import { useEffect } from 'react';

/**
 * Structured Data Component (JSON-LD)
 * Adds schema.org structured data for better SEO
 */
export default function StructuredData({ type = 'website', data = {} }) {
  useEffect(() => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    
    // Organization schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "InteriorDesignBusiness",
      "name": "Protek Studio Design",
      "description": "Design studio specializing in elegant interiors and luxury spaces for projects of all scales—from boutique spaces to large ventures and commercial buildings",
      "url": baseUrl,
      "logo": `${baseUrl}/logo512.png`,
      "image": `${baseUrl}/logo512.png`,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "IN"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+91-9030645554",
        "contactType": "Customer Service",
        "email": "protekdesignstudio@gmail.com"
      },
      "sameAs": [
        // Add social media links if available
      ],
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "serviceType": [
        "Interior Design",
        "Hotel Design",
        "Residential Design",
        "Commercial Design",
        "Design Consultation"
      ]
    };

    // Website schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Protek Studio Design",
      "url": baseUrl,
      "description": "Design studio specializing in elegant interiors and luxury spaces for projects of all scales—from boutique spaces to large ventures and commercial buildings",
      "publisher": {
        "@type": "Organization",
        "name": "Protek Studio Design"
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${baseUrl}/gallery?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };

    // Breadcrumb schema (if on a specific page)
    const breadcrumbSchema = data.breadcrumbs ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": data.breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `${baseUrl}${item.url}`
      }))
    } : null;

    // Service schema (for services page)
    const serviceSchema = data.services ? {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Interior Design Services",
      "provider": {
        "@type": "InteriorDesignBusiness",
        "name": "Protek Studio Design"
      },
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Interior Design Services",
        "itemListElement": data.services.map((service, index) => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": service.name,
            "description": service.description
          }
        }))
      }
    } : null;

    // Add schemas to page
    const schemas = [
      organizationSchema,
      websiteSchema,
      breadcrumbSchema,
      serviceSchema
    ].filter(Boolean);

    schemas.forEach((schema, index) => {
      const scriptId = `structured-data-${index}`;
      let script = document.getElementById(scriptId);
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(schema, null, 2);
    });

    // Cleanup function
    return () => {
      schemas.forEach((_, index) => {
        const scriptId = `structured-data-${index}`;
        const script = document.getElementById(scriptId);
        if (script) {
          script.remove();
        }
      });
    };
  }, [data]);

  return null;
}
